<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";
import { usePGRESTAPIStore } from "../store/pgrest_api";
import PGRESTResult from "../classes/PGRESTResult";
import { Avatar, Bell, Edit, Delete, Document, Plus, RefreshLeft, CircleCheck, CircleClose, View, Watch } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { storeToRefs } from "pinia";
import { useMainStore } from "../store/main";
import { useRouter } from "vue-router";
import UserType from "../enums/UserType";
import { useSort } from "../composables/sort";
import { useI18n } from "vue-i18n";
import AddEdit_ROB from "../components/AddEdit_ROB.vue";
import RegisterOfBonds from "../classes/DB_Entities/RegisterOfBond";
import { useGenericMethodsVariables } from "../composables/genericMethodsVariables";
import ChangeApprovalStatus from "../enums/ChangeApprovalStatus";

const { showError, showSuccess } = useGenericMethodsVariables();
let filterHideRejected = ref(true);
let filterPendingNew = ref(false);
const { locale } = useI18n({ useScope: "global" });
const pgrestapi = usePGRESTAPIStore();
const mainStore = useMainStore();
const router = useRouter();
const { globalPortfolioID } = storeToRefs(mainStore);
const { globalDebtorID } = storeToRefs(mainStore);
const { globalLoanAgreementID } = storeToRefs(mainStore);
const { onSortChange, sortColumn, sortOrder } = useSort();
const products = ref<any>([]);

let isNewEntity = ref(false);
let filterPending = ref(false);
let dataList = ref([]);
let dataCount = ref(0);
let isLoading = ref(false);
let lastPage = ref(0);
let addRegisterOfBond = ref(false);
let detailsVisible = ref(false);
let selectedRow = ref(0);
let deleteVisible = ref(false);
let DeleteSelectedRow = ref(0);
const DeleteDialogVisible = ref(false);
let filterRepresentative = ref<string>("");
let filterProduct = ref<string>("");
let filterAATitle = ref<string>("");
let filterFullName = ref<string>("");
let filterVAT = ref<string>("");
let activeLang = ref<string>("en");
let viewOnly = ref(false);

let timer = ref<any>(null);

watch(globalPortfolioID, async () => {
  if (mainStore.loggedUser.username !== '') {
    await loadProducts();
    await loadPage(1);
  }
});

watch(globalDebtorID, async () => {
  if (mainStore.loggedUser.username !== '') {
    await loadPage(1);
  }
});

watch(globalLoanAgreementID, async () => {
  if (mainStore.loggedUser.username !== '') {
    await loadPage(1);
  }
});

watch(locale, async () => {
  await loadProducts();
  await loadPage(1);
});

const handleClick = (row: any) => {
  let viewOnly = !isEditable(row);
    router.push({ name: "Register of Bonds Selection", state: { id: row.id, view_only: viewOnly } });
};

const deleteHandleClick = (row_id: number) => {
  DeleteSelectedRow.value = row_id;
  deleteVisible.value = true;
  DeleteRegisterOfBond(row_id);
};

const onAdd = async () => {
  showSuccess("Register of bond added Successfully", 1000);
  addRegisterOfBond.value = false;
  loadPage(lastPage.value);
};

const DeleteRegisterOfBond = async (row_id: number) => {
  let result: PGRESTResult = new PGRESTResult();

  let id = JSON.stringify(row_id);

  result = await pgrestapi.delete_data("register_of_bonds?id=eq." + id);

  if (result.error) {
    showError(result.error.message);

    DeleteDialogVisible.value = false;
    return;
  }

  onDelete();
};

onMounted(async () => {
  await loadProducts();
  loadPage(1);
});

const loadPage = async (page: number) => {
  activeLang.value = locale.value as string;
  isLoading.value = true;

  lastPage.value = page;
  page = page - 1;

  let filterUrl = "";

  if (filterFullName.value !== "") {
    filterUrl += `&fullname=ilike.*${filterFullName.value}*`;
  }

  if (filterVAT.value !== "") {
    filterUrl += `&vat_number=ilike.*${filterVAT.value}*`;
  }

  if (filterRepresentative.value !== "") {
    filterUrl += `&payment_representative=ilike.*${filterRepresentative.value}*`;
  }

  if (filterPending.value) {
    filterUrl +=
      "&or=(pending_approvals.gt.0,pending_approvals_banks.gt.0,pending_approvals_titles.gt.0,pending_approvals_bonds.gt.0)";
  }

  if (filterPendingNew.value) {
    filterUrl += "&approve_status=eq.0";
  }

  if (filterHideRejected.value) {
    filterUrl += "&approve_status=neq.2";
  }

  if (filterProduct.value !== "") {
    if (activeLang.value === "en") {
      filterUrl += `&product_id=eq.${filterProduct.value}`;
    } else if (activeLang.value === "el") {
      filterUrl += `&product_id=eq.${filterProduct.value}`;
    }
  }

  if (filterAATitle.value !== "") {
    filterUrl += `&ar_titlou=in.(${filterAATitle.value})`;
  }

  if (sortOrder.value !== "") {
    // if (filterUrl === "") {
    //   filterUrl = "?";
    // }
    filterUrl += `&order=${sortColumn.value}.${sortOrder.value}`;
  }

  let selectedPortfolio = "";
  let selectedDebtor = "";
  let selectedLoanAgreement = "";

  let addedFilter = "";

  if (mainStore.globalPortfolioID !== "") {
    selectedPortfolio = mainStore.globalPortfolioID;
    addedFilter += "&portfolio=eq." + selectedPortfolio;
  }

  if (mainStore.globalDebtorID !== "") {
    selectedDebtor = mainStore.globalDebtorID;
    addedFilter += "&debtor_id=eq." + selectedDebtor;
  }

  if (mainStore.globalLoanAgreementID !== "") {
    selectedLoanAgreement = mainStore.globalLoanAgreementID;
    addedFilter += "&loan_agreement_id=eq." + selectedLoanAgreement;
  }

  if (mainStore.loggedUser.user_type == UserType.Agent) {
    addedFilter += `&allowed_users=cs.{${mainStore.loggedUser.id}}`;
  } else if (mainStore.loggedUser.user_type == UserType.Supervisor) {
    addedFilter += `&portfolio_allowed_users=cs.{${mainStore.loggedUser.id}}`;
  }

  let result: PGRESTResult = await pgrestapi.getPaginated(
    "view_mosregisterofbonds_view1?select=*" + addedFilter + filterUrl,
    page
  );

  if (result.error) {
    showError(result.error.message);

    return;
  }

  if (result.headers)
    dataCount.value = parseInt(result.headers["content-range"].split("/")[1]);

  dataList.value = result.data;

  isLoading.value = false;
};

const onDelete = async () => {
  selectedRow.value = -1;
  ElMessage({
    showClose: true,
    message: "Bond Deleted Successfully",
    type: "success",
    duration: 1000,
  });

  DeleteDialogVisible.value = false;
  loadPage(lastPage.value);
};

const onFailSave = async () => {
  showError("Register of Bond failed to update", 2000);
  detailsVisible.value = false;
};

const filterChanged = async (value: string) => {
  if (timer.value !== 0) {
    clearTimeout(timer.value);
  }

  timer.value = setTimeout(() => {
    loadPage(1);

    timer.value = 0;
  }, 500);
};

const localSortChange = async (sortObj: any) => {
  onSortChange(sortObj);
  await loadPage(lastPage.value);
};

const pendingFilterChange = async () => {
  await loadPage(1);
};

const loadProducts = async () => {
  let result: PGRESTResult = new PGRESTResult();

  result = await pgrestapi.get("config_product?order=product");

  if (result.error) {
    showError(result.error.message);

    return;
  }

  products.value = [];
  for (let i = 0; i < result.data.length; i++) {
    if (locale.value == "en") {
      products.value.push({
        value: result.data[i].id,
        label: result.data[i].english_trans,
      });
    } else if (locale.value == "el") {
      products.value.push({
        value: result.data[i].id,
        label: result.data[i].greek_trans,
      });
    }
  }

  // console.log(products);
};

const handleClickAdd = () => {
  addRegisterOfBond.value = true;
  isNewEntity.value = true;
  selectedRow.value = -1;
};

const onCloseAddEdit = () => {
  addRegisterOfBond.value = false;
  selectedRow.value = -1;
};

const entityApprove = async (entity_id: number, accepted: boolean) => {
  console.log("entity id", entity_id, "approved", accepted);
  if (entity_id === undefined || entity_id === null) {
    return;
  }

  let result = await pgrestapi.patch(`${RegisterOfBonds.getTableNameStatic()}?id=eq.${entity_id}`, { approve_status: accepted ? ChangeApprovalStatus.Approved : ChangeApprovalStatus.Rejected });

  console.log("entity_approve", RegisterOfBonds.getTableNameStatic(), entity_id);

  if (result.error) {
    showError(`Failed to update Register of Bond: ${result.error.message}`, 2000);
  } else {
    showSuccess(`Register of Bond${!accepted ? " not " : " "}approved`, 1000);
  }

  await loadPage(lastPage.value);
};

const isEditable = (row: any) => {
  return (row.debtor_approve_status === 1 && row.loan_agreement_approve_status === 1);
}
</script>

<template>
  <div class="content-wrapper" v-loading="isLoading">
    <el-row :gutter="10">
      <el-col :span="12">
        <h3 class="heading-page">
          {{ $t("RegisterOfBondsView.register_of_bonds") }} ({{ dataCount }})
        </h3>
      </el-col>
    </el-row>

    <div style="margin: 30px">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input v-model="filterFullName" class="client-input" :placeholder="$t('DebtorView.company_name')"
            @input="filterChanged" />
        </el-col>
        <el-col :span="6">
          <el-input v-model="filterVAT" class="client-input" :placeholder="$t('DebtorView.tax_registration_nbr')"
            @input="filterChanged" />
        </el-col>
        <el-col :span="6">
          <el-input v-model="filterRepresentative" class="client-input"
            :placeholder="$t('RegisterOfBondsView.payment_representative')" @input="filterChanged" />
        </el-col>
        <el-col :span="6">
          <el-select clearable v-model="filterProduct" class="m-2 client-input"
            :placeholder="$t('SummaryCheckView.product')" value-key="id" @change="filterChanged" style="width: 100%">
            <el-option v-for="item in products" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-col>
      </el-row>
    </div>

    <div>
      <el-row :gutter="10" style="margin: 10px; margin-top: 15px">
        <el-col :span="12" class="realestate-add-new-btn">
          <el-switch v-model="filterPending" size="small" :active-text="$t('global.pending_changes')" inactive-text=""
            @change="pendingFilterChange()" style="float: left" />
          <el-switch v-model="filterPendingNew" size="small" :active-text="$t('global.pending_changes_new_entities')"
            inactive-text="" @change="pendingFilterChange()" style="float: left" />
          <el-switch v-model="filterHideRejected" size="small" :active-text="$t('global.hide_rejected_entities')"
            inactive-text="" @change="pendingFilterChange()" style="float: left" />
        </el-col>
        <el-col :span="12" class="realestate-add-new-btn">
          <el-button link type="primary" size="small" class="btn-add" @click="loadPage(lastPage)"
            style="float: right; margin: 2.5px">
            <el-icon>
              <RefreshLeft />
            </el-icon>
          </el-button>
          <el-button link type="primary" size="small" class="btn-add" style="float: right; margin: 2.5px"
            @click="handleClickAdd()" :icon="Plus" />
        </el-col>
      </el-row>
    </div>

    <el-table :data="dataList" stripe border table-layout="fixed" sortable="custom" @sort-change="localSortChange"
      size="small">
      <el-table-column sortable fixed prop="ar_sumbashs" :label="$t('RegisterOfBondsView.loan_agreement')" />
      <el-table-column fixed prop="fullname" :label="$t('DebtorView.company_name')" sortable />
      <el-table-column fixed prop="vat_number" :label="$t('DebtorView.tax_registration_nbr')" sortable />

      <el-table-column sortable fixed prop="payment_representative"
        :label="$t('RegisterOfBondsView.payment_representative')" />
      <el-table-column fixed prop="portf_name" :label="$t('DebtorDetailsView.portfolio')" sortable />
      <el-table-column v-if="activeLang === 'el'" fixed prop="greek_trans" :label="$t('LoanAgreementView.product')"
        sortable />
      <el-table-column v-if="activeLang === 'en'" fixed prop="english_trans" :label="$t('LoanAgreementView.product')"
        sortable />
      <el-table-column :label="$t('global.pending_approvals')" align="center">
        <el-table-column sortable fixed prop="pending_approvals" :label="$t('RegisterOfBondsView.register')" />
        <el-table-column sortable fixed prop="pending_approvals_banks" :label="$t('RegisterOfBondsView.banks')" />
        <el-table-column sortable fixed prop="pending_approvals_titles" :label="$t('RegisterOfBondsView.titles')" />
        <el-table-column sortable fixed prop="pending_approvals_bonds" :label="$t('RegisterOfBondsView.bonds')" />
      </el-table-column>

      <el-table-column fixed="right" :label="$t('global.operations')" width="280">
        <template #default="scope">
          <el-button link type="primary" size="small" class="btn-detail" @click="handleClick(scope.row)"
            :icon="isEditable(scope.row) ? Edit : View" />
          <el-tooltip v-if="scope.row.approve_status == 0 && mainStore.loggedUser.user_type === UserType.Agent"
            effect="dark" content="Pending Supervisor approval" placement="top">
            <el-button size="small" type="primary" class="btn-warn" :icon="Watch" circle />
          </el-tooltip>
          <el-button v-if="mainStore.loggedUser.user_type === UserType.Administrator" link type="primary" size="small"
            class="btn-delete" @click="DeleteDialogVisible = true; DeleteSelectedRow = scope.row.id;" :icon="Delete" />
          <span v-if="
            mainStore.loggedUser.user_type < UserType.Agent &&
            scope.row.approve_status === 0
          ">
            <el-button class="btn-accept_square" @click="entityApprove(scope.row.id, true)" :icon="CircleCheck" />
            <el-button class="btn-reject_square" @click="entityApprove(scope.row.id, false)" :icon="CircleClose" />
          </span>
          <el-popover v-if="scope.row.pending_approvals > 0" placement="top-start" title="" :width="155" trigger="hover"
            :content="$t('global.pending_changes')">
            <template #reference>
              <el-button :icon="Bell" circle size="small" style="background-color: coral; border: 0px; color: white" />
            </template>
          </el-popover>
          <el-popover v-if="scope.row.debtor_approve_status !== 1" placement="top-start" title="" :width="205"
            trigger="hover" :content="$t('global.debtor_not_approved')">
            <template #reference>
              <el-button :icon="Avatar" circle size="small"
                style="background-color: blueviolet; border: 0px; color: white" />
            </template>
          </el-popover>
          <el-popover v-if="scope.row.loan_agreement_approve_status !== 1" placement="top-start" title="" :width="265"
            trigger="hover" :content="$t('global.loan_agreement_not_approved')">
            <template #reference>
              <el-button :icon="Document" circle size="small"
                style="background-color: coral; border: 0px; color: white" />
            </template>
          </el-popover>
        </template>
      </el-table-column>
    </el-table>

    <div>
      <el-pagination size="small" layout="prev, pager, next" :page-size="10" :total="dataCount" @current-change="loadPage" />
    </div>

    <el-dialog v-if="addRegisterOfBond" v-model="addRegisterOfBond" @update:model-value="onCloseAddEdit" title="Add Register Of Bonds">
      <AddEdit_ROB :rob_id="selectedRow" :new-entity="isNewEntity" @close="onCloseAddEdit" @save="onAdd"
        @fail-save="onFailSave" :view_only="viewOnly" />
    </el-dialog>

    <el-dialog v-model="DeleteDialogVisible" title="Register of Bond Delete" width="50%">
      <span>Are you sure you want to delete this Bond ?</span>
      <template #footer #default="scope">
        <span class="dialog-footer">
          <el-button @click="DeleteDialogVisible = false">{{ $t('generic.cancel') }}</el-button>
          <el-button type="primary" @click="deleteHandleClick(DeleteSelectedRow)">{{ $t('generic.delete') }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped></style>
